import { Box, IconButton, Option, Button } from "@mui/joy";
import DarkSelect from "./DarkSelect";
import DarkTypography from "./DarkTypography";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FirstPageIcon,
  LastPageIcon,
} from "../../styles/StyledMUI";
import { colorTokens } from "../../styles/globalStyles";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  loading?: boolean;
}

export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  loading = false,
}: PaginationProps) {
  if (totalPages <= 1 && totalItems === 0) return null;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const limitOptions = [5, 10, 20, 50];

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxButtons - 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const labelSmSx = { fontSize: { xs: "0.75rem", sm: "0.8rem" } };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 1, sm: 2 },
        py: 1,
        backgroundColor: colorTokens.bg.surface,
        borderTop: `1px solid ${colorTokens.border.default}`,
        flexWrap: "wrap",
        minHeight: "44px",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          justifyContent: { xs: "space-between", sm: "flex-start" },
          order: { xs: 2, sm: 1 },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <DarkTypography
          level="body-sm"
          colorVariant="primary"
          sx={{ whiteSpace: "nowrap", ...labelSmSx }}
        >
          Par page:
        </DarkTypography>
        <DarkSelect
          value={itemsPerPage}
          onChange={(_, value) => value && onItemsPerPageChange(value)}
          size="sm"
          disabled={loading}
          sx={{
            minWidth: { xs: 50, sm: 55 },
            minHeight: { xs: "28px", sm: "30px" },
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
          }}
        >
          {limitOptions.map((limit) => (
            <Option key={limit} value={limit}>
              {limit}
            </Option>
          ))}
        </DarkSelect>

        {totalItems > 0 && (
          <DarkTypography
            level="body-sm"
            colorVariant="primary"
            sx={{
              whiteSpace: "nowrap",
              ...labelSmSx,
              fontWeight: 500,
              display: { xs: "block", sm: "none" },
            }}
          >
            {startItem}-{endItem} sur {totalItems}
          </DarkTypography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.3, sm: 0.5 },
          order: { xs: 1, sm: 2 },
          justifyContent: { xs: "center", sm: "flex-start" },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <IconButton
          variant="solid"
          size="sm"
          disabled={currentPage === 1 || loading}
          onClick={() => onPageChange(1)}
          aria-label="Première page"
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            color: colorTokens.text.primary,
            minWidth: { xs: "28px", sm: "30px" },
            height: { xs: "28px", sm: "30px" },
            "&:hover": {
              backgroundColor: colorTokens.accent.primary,
              color: colorTokens.accent.contrastText,
            },
            "&:disabled": {
              backgroundColor: colorTokens.bg.surface,
              color: colorTokens.text.muted,
            },
            display: { xs: totalPages > 5 ? "none" : "flex", sm: "flex" },
          }}
        >
          <FirstPageIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
        </IconButton>

        <IconButton
          variant="solid"
          size="sm"
          disabled={currentPage === 1 || loading}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Page précédente"
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            color: colorTokens.text.primary,
            minWidth: { xs: "30px", sm: "32px" },
            height: { xs: "30px", sm: "32px" },
            "&:hover": {
              backgroundColor: colorTokens.accent.primary,
              color: colorTokens.accent.contrastText,
            },
            "&:disabled": {
              backgroundColor: colorTokens.bg.surface,
              color: colorTokens.text.muted,
            },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </IconButton>

        {totalPages > 1 &&
          getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              size="sm"
              variant={pageNum === currentPage ? "solid" : "outlined"}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              aria-label={`Page ${pageNum}`}
              aria-current={pageNum === currentPage ? "page" : undefined}
              sx={{
                minWidth: { xs: 26, sm: 28 },
                height: { xs: "28px", sm: "30px" },
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                color:
                  pageNum === currentPage
                    ? colorTokens.accent.contrastText
                    : colorTokens.text.primary,
                backgroundColor:
                  pageNum === currentPage
                    ? colorTokens.accent.primary
                    : colorTokens.bg.surfaceMuted,
                borderColor:
                  pageNum === currentPage ? colorTokens.accent.primary : colorTokens.border.default,
                "&:hover": {
                  backgroundColor: colorTokens.accent.primary,
                  color: colorTokens.accent.contrastText,
                  borderColor: colorTokens.accent.primary,
                },
              }}
            >
              {pageNum}
            </Button>
          ))}

        <IconButton
          variant="solid"
          size="sm"
          disabled={currentPage === totalPages || loading}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Page suivante"
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            color: colorTokens.text.primary,
            minWidth: { xs: "30px", sm: "32px" },
            height: { xs: "30px", sm: "32px" },
            "&:hover": {
              backgroundColor: colorTokens.accent.primary,
              color: colorTokens.accent.contrastText,
            },
            "&:disabled": {
              backgroundColor: colorTokens.bg.surface,
              color: colorTokens.text.muted,
            },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </IconButton>

        <IconButton
          variant="solid"
          size="sm"
          disabled={currentPage === totalPages || loading}
          onClick={() => onPageChange(totalPages)}
          aria-label="Dernière page"
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            color: colorTokens.text.primary,
            minWidth: { xs: "28px", sm: "30px" },
            height: { xs: "28px", sm: "30px" },
            "&:hover": {
              backgroundColor: colorTokens.accent.primary,
              color: colorTokens.accent.contrastText,
            },
            "&:disabled": {
              backgroundColor: colorTokens.bg.surface,
              color: colorTokens.text.muted,
            },
            display: { xs: totalPages > 5 ? "none" : "flex", sm: "flex" },
          }}
        >
          <LastPageIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
        </IconButton>
      </Box>

      {totalItems > 0 && (
        <DarkTypography
          level="body-sm"
          colorVariant="primary"
          sx={{
            whiteSpace: "nowrap",
            fontSize: "0.8rem",
            fontWeight: 500,
            order: 3,
            display: { xs: "none", sm: "block" },
          }}
        >
          {startItem}-{endItem} sur {totalItems}
        </DarkTypography>
      )}
    </Box>
  );
}
